import React from 'react';
import {
  TOURNAMENT_CREATE_EXTERNAL_PAGE,
  TOURNAMENT_CREATE_PAGE,
  TOURNAMENT_QUICK_CREATE_PAGE,
  VAIR_RATING_SESSION_CREATE_PAGE,
} from 'constants/pages';
import { EventTypesEnum } from 'types/generated/client';
import type { RichText } from './props';

export type LearnMoreKey = 'tournament' | 'quickRoundRobin' | 'ratingSession';

export const EVENT_TYPE_LEARN_MORE: Record<
  LearnMoreKey,
  {
    title: string;
    description: RichText;
    icon: string;
    iconClassName?: string;
    getStartedHref: string;
  }
> = {
  tournament: {
    title: 'Tournament',
    description: [
      {
        text: 'A competitive event that starts with pool play (round robin), then finishes with a single-elimination playoff bracket (knockout stage). Scores are tracked in ',
      },
      { text: 'Bounce', bold: true },
      { text: ' and can be automatically submitted to ' },
      { text: 'VAIR', bold: true },
      { text: ' if the organizer chooses.' },
    ],
    icon: '/images/vair/vair-tournament-lg.png',
    getStartedHref: TOURNAMENT_CREATE_PAGE,
  },
  quickRoundRobin: {
    title: 'Round Robin',
    description: [
      {
        text: "A set of scheduled games where everyone rotates through opponents for a balanced mix of matchups. There is no knockout/playoff bracket — it's purely round-robin play. Organizers can choose to create a ",
      },
      { text: 'VAIR', bold: true },
      { text: ' round robin if they want official ' },
      { text: 'VAIR', bold: true },
      { text: ' scores submitted, or a friendly round robin with no ratings.' },
    ],
    icon: '/images/vair/vair-round-robin.png',
    getStartedHref: TOURNAMENT_QUICK_CREATE_PAGE,
  },
  ratingSession: {
    title: 'Rating Session',
    description: [
      { text: 'Run an official ' },
      { text: 'VAIR', bold: true },
      {
        text: ' rating session with structured match play designed to generate ratings. Track scores and submit results to VAIR after the session.',
      },
    ],
    icon: '/images/vair/vair-black.png',
    iconClassName: 'mb-ds-2xl h-12 w-auto object-contain',
    getStartedHref: VAIR_RATING_SESSION_CREATE_PAGE,
  },
};

export const EVENT_TYPES: {
  title: string;
  isVairIntegrated: boolean;
  icon: string;
  getStartedHref: string;
  type: EventTypesEnum;
  learnMoreKey: LearnMoreKey;
  /**
   * VAIR-only event type: creating one needs a connected VAIR account with the
   * right role, so EventTypeModal runs it through the VAIR check. Tournament and
   * Quick Round Robin stay open to everyone — their "VAIR integrated" badge means
   * scores *can* be submitted, not that VAIR is required.
   */
  isRatingSession?: boolean;
}[] = [
  {
    title: 'Tournament',
    isVairIntegrated: true,
    icon: '/images/vair/vair-tournament.png',
    getStartedHref: TOURNAMENT_CREATE_PAGE,
    type: EventTypesEnum.Tournament,
    learnMoreKey: 'tournament',
  },
  {
    title: 'Quick Round Robin',
    isVairIntegrated: true,
    icon: '/images/vair/vair-round-robin.png',
    getStartedHref: TOURNAMENT_QUICK_CREATE_PAGE,
    type: EventTypesEnum.GeneralEvent,
    learnMoreKey: 'quickRoundRobin',
  },
  // {
  //   title: 'League',
  //   isVairIntegrated: true,
  //   icon: '/images/vair/vair-league.png',
  //   getStartedHref: '',
  // },
  {
    title: 'Rating Session',
    isVairIntegrated: false,
    icon: '/images/vair/vair-white.png',
    getStartedHref: VAIR_RATING_SESSION_CREATE_PAGE,
    isRatingSession: true,
    type: EventTypesEnum.GeneralEvent,
    learnMoreKey: 'ratingSession',
  },
];

export type EventTypeLearnMoreItem = {
  title: string;
  description: RichText;
  icon: React.ReactNode;
  getStartedHref: string;
  /** VAIR-only flow — "Get started" runs through the VAIR check. */
  isRatingSession?: boolean;
};

export const ALL_EVENTS_LEARN_MORE: EventTypeLearnMoreItem[] = [
  {
    title: 'Tournament',
    description: [
      {
        text: 'Run a competitive event with pool play (round robin) followed by a single-elimination playoff bracket. Paid registration is supported and scores can be submitted to ',
      },
      { text: 'VAIR', bold: true },
      { text: '.' },
    ],
    icon: (
      <img
        src="/images/vair/vair-tournament.png"
        alt=""
        className="h-14 w-auto object-contain"
        aria-hidden
      />
    ),
    getStartedHref: TOURNAMENT_CREATE_PAGE,
  },
  {
    title: 'Quick Round Robin',
    description: [
      {
        text: 'Set up a simple round robin where everyone rotates through opponents for balanced matchups. Quick Round Robins are free (no payment collection in Bounce), and you can run it as friendly play or submit scores to ',
      },
      { text: 'VAIR', bold: true },
      { text: '.' },
    ],
    icon: (
      <img
        src="/images/vair/vair-round-robin.png"
        alt=""
        className="h-16 w-auto object-contain"
        aria-hidden
      />
    ),
    getStartedHref: TOURNAMENT_QUICK_CREATE_PAGE,
  },
  {
    title: 'Rating Session',
    description: [
      { text: 'Run an official ' },
      { text: 'VAIR', bold: true },
      {
        text: ' rating session with structured match play designed to generate ratings. Track scores and submit results to VAIR after the session.',
      },
    ],
    icon: (
      <img
        src="/images/vair/vair-black.png"
        alt=""
        className="h-6 w-auto object-contain"
        aria-hidden
      />
    ),
    getStartedHref: VAIR_RATING_SESSION_CREATE_PAGE,
    isRatingSession: true,
  },
  {
    title: 'External event',
    description: [
      {
        text: "Already using another platform for registration? Keep it. Add your event to Bounce so players can discover it here, then we'll send them to your external registration page.",
      },
    ],
    icon: (
      <img
        src="/images/app/attach-gradient.svg"
        alt=""
        className="h-10 w-10 object-contain"
        aria-hidden
      />
    ),
    getStartedHref: TOURNAMENT_CREATE_EXTERNAL_PAGE,
  },
];
